// Two-step sign-in (TOTP), the client half.
//
// A correct password, or a Google / Apple / Microsoft sign-in, for somebody with
// an authenticator answers { mfa_required: true, mfa_token } instead of a token.
// The person then types a code from their app (or a backup code), and
// /mfa/verify/ swaps the mfa_token for the real token. The mfa_token is short-lived
// and single-purpose; it is never a session.
import { apiUrl } from "../lib/api";

const PENDING_KEY = "sift_pending_mfa";

/** True when an auth response wants the code step instead of signing in. */
export function needsMfa(data) {
  return Boolean(data && data.mfa_required && data.mfa_token);
}

/** Hand a pending code step to the sign-in page (used by sign-ins elsewhere). */
export function stashPendingMfa(data) {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({ token: data.mfa_token, at: Date.now() }));
  } catch { /* private mode: they sign in again */ }
}

/** The pending code step, once, if it is still fresh (the server's own expiry decides). */
export function takePendingMfa() {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    sessionStorage.removeItem(PENDING_KEY);
    if (!raw) return null;
    const { token, at } = JSON.parse(raw);
    return token && Date.now() - at < 10 * 60 * 1000 ? token : null;
  } catch {
    return null;
  }
}

/** Swap the mfa_token and a code for the real sign-in response. Throws with the server's message. */
export async function verifyMfa(mfaToken, code) {
  const res = await fetch(apiUrl("/api/accounts/mfa/verify/"), {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ mfa_token: mfaToken, code: (code || "").replace(/\s+/g, "") }),
    credentials: "omit",
    mode: "cors",
  });
  let data = null;
  try { data = await res.json(); } catch { /* empty body */ }
  if (!res.ok) {
    throw new Error((data && (data.error || data.detail)) || "That code didn't work. Try again.");
  }
  return data;
}
